//
// # Properties Panel draft model
//
// The buffered, locally-edited copy of the selected entity's editable fields.
// `buildDraft` seeds it from a descriptor; `hasDirtyFields` compares draft vs
// descriptor to drive Apply/Revert enablement; `draftToEdits` produces the
// `EntityEdits` payload carrying only the changed fields.
//

import { paramValueToString } from "CoreUtils";
import type { schema } from "GenAlphaSchema";
import type { GeneratorParamSpec } from "ServerInterface";
import type {
  DescriptorPoint,
  EntityDescriptor,
  EntityEdits,
} from "../../types";

export interface Draft {
  readonly name: string;
  readonly position: DescriptorPoint;
  readonly rotation: number;
  readonly reflected: boolean;
  // Schematic pin direction (undefined for non-pin kinds).
  readonly direction?: schema.core.PortDir;
  // Layout entities.
  readonly layer?: number;
  readonly pinType?: schema.core.PinType;
  // Generator params (instances).
  readonly params?: Record<string, string>;
  // Pending instance type change (schematic): the chosen new type name and its
  // param spec. When set and different from the descriptor's typeName, Apply
  // performs a delete+create instead of a field-edit batch.
  readonly newTypeName?: string;
}

// Seed a draft from a descriptor. Returns null for kinds without editable
// fields (null / multi / unsupported).
export function buildDraft(descriptor: EntityDescriptor | null): Draft | null {
  if (descriptor === null) return null;
  switch (descriptor.kind) {
    case "instance":
      return {
        name: descriptor.name,
        position: descriptor.position,
        rotation: descriptor.rotation,
        reflected: descriptor.reflected,
        params: paramsToStrings(descriptor.params, descriptor.paramSpec),
      };
    case "schPin":
      return {
        name: descriptor.name,
        position: descriptor.position,
        rotation: descriptor.rotation,
        reflected: descriptor.reflected,
        direction: descriptor.direction,
      };
    case "layoutInstance":
      return {
        name: descriptor.name,
        position: descriptor.position,
        rotation: descriptor.rotation,
        reflected: descriptor.reflected,
        layer: descriptor.layer,
        params: paramsToStrings(descriptor.params, descriptor.paramSpec),
      };
    case "layoutPin":
      return {
        name: descriptor.name,
        position: descriptor.position,
        rotation: 0,
        reflected: false,
        layer: descriptor.layer,
        pinType: descriptor.pinType,
      };
    case "multi":
    case "unsupported":
      return null;
  }
}

// Whether any editable field in the draft differs from the descriptor baseline.
export function hasDirtyFields(
  descriptor: EntityDescriptor,
  draft: Draft
): boolean {
  const baseline = buildDraft(descriptor);
  if (baseline === null) return false;
  if (draft.name !== baseline.name) return true;
  if (
    draft.position.x !== baseline.position.x ||
    draft.position.y !== baseline.position.y
  ) {
    return true;
  }
  if (draft.rotation !== baseline.rotation) return true;
  if (draft.reflected !== baseline.reflected) return true;
  if (draft.direction !== baseline.direction) return true;
  if (draft.layer !== baseline.layer) return true;
  if (draft.pinType !== baseline.pinType) return true;
  if (paramsDiffer(draft.params, baseline.params)) return true;
  if (draft.newTypeName !== undefined) return true;
  return false;
}

// Build an EntityEdits payload carrying only the fields that changed.
export function draftToEdits(
  descriptor: EntityDescriptor,
  draft: Draft
): EntityEdits | null {
  if (descriptor.kind === "multi" || descriptor.kind === "unsupported") {
    return null;
  }
  const baseline = buildDraft(descriptor);
  if (baseline === null) return null;

  const edits: {
    -readonly [K in keyof EntityEdits]: EntityEdits[K];
  } = {
    designIdent: descriptor.designIdent,
    info: {
      entityKind: kindToEntityKind(descriptor.kind),
      id: descriptor.id,
      count: 1,
    },
  };

  if (draft.name !== baseline.name) edits.name = draft.name;
  if (
    draft.position.x !== baseline.position.x ||
    draft.position.y !== baseline.position.y
  ) {
    edits.position = draft.position;
  }
  if (draft.rotation !== baseline.rotation) edits.rotation = draft.rotation;
  if (draft.reflected !== baseline.reflected) {
    edits.reflected = draft.reflected;
  }
  if (draft.direction !== baseline.direction && draft.direction !== undefined) {
    edits.direction = draft.direction;
  }
  if (draft.layer !== baseline.layer && draft.layer !== undefined) {
    edits.layer = draft.layer;
  }
  if (draft.pinType !== baseline.pinType && draft.pinType !== undefined) {
    edits.pinType = draft.pinType;
  }
  if (paramsDiffer(draft.params, baseline.params) && draft.params) {
    edits.params = draft.params;
  }

  return edits;
}

// Map the descriptor kind back to the EditorCore EntityKind string.
function kindToEntityKind(
  kind: "instance" | "schPin" | "layoutInstance" | "layoutPin"
): string {
  switch (kind) {
    case "instance":
      return "Instance";
    case "schPin":
      return "SchPin";
    case "layoutInstance":
      return "LayoutInstance";
    case "layoutPin":
      return "LayoutPin";
  }
}

// Convert a params record to a string-valued record for form inputs, filling
// in each spec default the instance doesn't store.
//
// An instance records only the parameters someone actually set (see
// `instanceBuilder`'s `instanceParams`), so without this pass every untouched
// parameter renders as an empty box — `ParamFields` reads
// `paramValues[name] ?? ""` and has no default of its own to fall back on.
function paramsToStrings(
  params: Record<string, unknown> | undefined,
  spec: GeneratorParamSpec | undefined
): Record<string, string> | undefined {
  if (params === undefined && spec === undefined) return undefined;
  const out: Record<string, string> = {};
  for (const p of spec?.params ?? []) {
    if (p.default !== undefined) {
      out[p.name] = paramValueToString(p.default).unwrapOr("");
    }
  }
  for (const [key, value] of Object.entries(params ?? {})) {
    // Non-primitive values are unexpected for a form field; fall back to empty.
    out[key] = paramValueToString(value).unwrapOr("");
  }
  return out;
}

// Whether two param string-records differ.
function paramsDiffer(
  a: Record<string, string> | undefined,
  b: Record<string, string> | undefined
): boolean {
  if (a === undefined && b === undefined) return false;
  if (a === undefined || b === undefined) return true;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (a[key] !== b[key]) return true;
  }
  return false;
}
